import React, { useMemo } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ServerError from '../../../../../components/ServerError';
import {
  MyTimeSheetDocument,
  MyTimeTrackingDocument,
  useMyTimeSheetQuery,
} from '../../../__generated__/graphql';
import TimeTrackingEntries from '../TimeTrackingEntries';
import ChartsRow from './ChartsRow';
import Loading from './Loading';
import Nav from './Nav';
import NoTimeSheet from './NoTimeSheet';

import { getTimeSheetTitle } from './utils';
import { toISODateOnly } from '../../../../../utils/dateUtils';
import { useUserInfo } from '../../../context/UserInfo';
import { TimeTrackingProvider } from '../../../hooks/timeTrackingSettings/TimeTrackingContext';
import { mapProject } from '../../Projects/mapping';
import { ProjectWithStatus } from '../../Projects/types';
import { getProjectStatus } from '../../Projects/ProjectsTimesheets/helpers';
import moment from 'moment';

const DATE_PARAM = 'startDate';

type Params = {
  startDate: string;
  title: string;
  previousTimeSheetStartDay: string;
  nextTimeSheetStartDay: string;
};

const CardHeader: React.FC<Params> = (props) => {
  const { t } = useTranslation();

  return useMemo(
    () => (
      <Card.Header>
        <Row>
          <Col>
            <h4 className="mt-2">{t('timeTracking.overview.title')}</h4>
          </Col>
          <Col className="col-auto text-end">
            <Nav {...props} />
          </Col>
        </Row>
      </Card.Header>
    ),
    [props, t],
  );
};

export const createTimeSheetPageQueryParams = ({
  startDate,
}: {
  startDate: string;
}) => ({
  [DATE_PARAM]: startDate,
});

const MyTimeSheet: React.FC = () => {
  const query: URLSearchParams = new URLSearchParams(useLocation().search);
  const startDate = query.get(DATE_PARAM) ?? toISODateOnly(new Date());

  const { t } = useTranslation();
  const { id: meId } = useUserInfo();

  const { data, loading, error } = useMyTimeSheetQuery({
    variables: {
      employeeId: meId,
      day: startDate,
    },
  });

  const timeSheet = data?.timeSheetDay?.timeSheet;

  const projects: ProjectWithStatus[] = useMemo(() => {
    const projectsData = data?.projects;
    if (!projectsData) return [];
    return projectsData.map((project) => {
      const mapped = mapProject(project);
      return {
        ...mapped,
        status: getProjectStatus(mapped, moment(startDate)),
      };
    });
  }, [data, startDate]);

  //TODO:: stop using refetchQueries
  const refetchQueries = [
    {
      query: MyTimeTrackingDocument,
    },
    {
      query: MyTimeSheetDocument,
      variables: {
        employeeId: meId,
        day: startDate,
      },
    },
  ];

  const title = timeSheet
    ? getTimeSheetTitle(
        t,
        timeSheet.flair__Start_Date__c,
        timeSheet.flair__Type__c,
      )
    : '';

  const cardHeader = timeSheet ? (
    <CardHeader
      startDate={timeSheet.flair__Start_Date__c}
      previousTimeSheetStartDay={timeSheet.previousTimeSheetStartDay}
      nextTimeSheetStartDay={timeSheet.nextTimeSheetStartDay}
      title={title}
    />
  ) : null;

  if (loading && timeSheet === undefined) return <Loading />;

  if (error) return <ServerError />;

  if (!timeSheet) {
    return (
      <Card>
        {cardHeader}
        <Card.Body>
          <NoTimeSheet />
        </Card.Body>
      </Card>
    );
  }

  return (
    <TimeTrackingProvider employeeId={meId}>
      <Card>
        {cardHeader}
        <Card.Body>
          <ChartsRow timeSheet={timeSheet} />
        </Card.Body>
      </Card>
      <TimeTrackingEntries
        timeSheet={timeSheet}
        costCenters={data?.costCenters ?? []}
        refetchQueriesForEmployeeMode={refetchQueries}
        projects={projects}
      />
    </TimeTrackingProvider>
  );
};

export default MyTimeSheet;
